import type { ProgressEntry, StructuredLogger } from '@mimir/primitive/structuredLogger';
import type { Downloader, DownloadProgressHandler, MediaSegmentRange } from '@mimir/common/types/downloader';
import { ProgressPropagatingLogger } from '@mimir/common/log/progressPropagatingLogger';
import type { Target } from '@mimir/common/transfer/transferPipeline';
import type { MediaSegmentsUrlProvider } from '@mimir/common/transfer/createSourceChunkInfoStreamForSegmentedMedia';
import { createSourceChunkInfoStreamForSegmentedMedia } from '@mimir/common/transfer/createSourceChunkInfoStreamForSegmentedMedia';
import { TransferPipeline, createFileTarget, createStreamTarget } from '@mimir/common/transfer/transferPipeline';
import type { Vfs } from '@mimir/common/types/vfs';
import type { ResourceAccessor } from '@mimir/common/io/resource';
import { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import { writableFileStream } from '@mimir/common/io/fileStream';
import { tsSegmentRangeMxfRewrapTransfer } from '@mimir/common/transcoding/mxf/rewrapTransfer';
import type { MxfRewrapItemProvider } from '@mimir/common/transcoding/mxf/rewrapInputs';
import type { HighPrecisionMonotonicClock } from '@mimir/common/types/highPrecisionMonotonicClock';
import type { UrlProvider } from '@mimir/common/io/urlProvider';
import { createMimirSegmentDelimiterPacket } from '@mimir/common/mpegts/tsToFragmentedMp4TransformStream';

export type TsToFragmentedMp4TransmuxerStreamFactory = () => TransformStream<Uint8Array, Uint8Array>;

export interface DefaultDownloaderServices {
  mediaSegmentsUrlProvider: MediaSegmentsUrlProvider,
  logger: StructuredLogger,
  resourceAccessor: ResourceAccessor,
  mxfRewrapItemProvider: MxfRewrapItemProvider,
  vfs: Vfs,

  /**
   *  This is only needed for performance stats logging of the stream target
   *  used when the transfer pipeline is not writing directly to disk. Can
   *  be null if not available which will disable performance stats.
   */
  clock: HighPrecisionMonotonicClock | null,
  tsToFragmentedMp4TransmuxStreamFactory: TsToFragmentedMp4TransmuxerStreamFactory,
}

export function createDefaultDownloader(services: DefaultDownloaderServices): Downloader {
  return new DefaultDownloader(services);
}

class DefaultDownloader implements Downloader {
  private dummyAbortSignal = (new AbortController()).signal;
  constructor(private services: DefaultDownloaderServices) { }

  async downloadFlatFile(
    parameters: {
      urlProvider: UrlProvider,
      outputFilename: string,
      abortSignal?: AbortSignal,
      progressHandler?: DownloadProgressHandler,
      mediaSize?: number,
    },
  ): Promise<void> {
    const { resourceAccessor } = this.services;
    const { urlProvider, outputFilename, abortSignal, progressHandler } = parameters;
    const transferPipeline = this.createTransferPipeline(abortSignal);

    const mediaSize = await resourceAccessor.getSize({ type: 'http', provider: urlProvider });

    const logger = this.getProgressLogger(outputFilename, progressHandler);

    const url = urlProvider.getUrl();
    logger.log({ level: 'info', message: 'Starting flat file download', url, outputFilename });

    await transferPipeline.transferFlatFileReference(urlProvider, mediaSize, logger, outputFilename);

    logger.log({ level: 'info', message: 'Completed flat file download', url, outputFilename });
  }

  async  downloadTsSegmentStreamAsSingleFile(
    parameters: {
      itemId: string,
      streamRoleId: StreamRoleId.LiveRecordHighresTs | StreamRoleId.LiveRecordWebproxyTs | StreamRoleId.LiveRecordAudioOnlyHighresTs,
      segmentRange: MediaSegmentRange,
      outputFilename: string,
      remuxTo?: 'fmp4',
      abortSignal?: AbortSignal,
      progressHandler?: DownloadProgressHandler,
    }
  ): Promise<void> {
    const { itemId, streamRoleId, segmentRange, outputFilename, remuxTo, abortSignal, progressHandler } = parameters;
    const { mediaSegmentsUrlProvider, vfs, clock } = this.services;
    const progressId = outputFilename;
    const logger = this.getProgressLogger(progressId, progressHandler);
    logger.log({ message: 'progress', progress: { [progressId]: { position: 0, length: NaN, unit: 'bytes' } } });

    let target : Target;
    let flushToDiskPromise : Promise<void> | null = null;

    // Bytes to indicate segment boundaries in target TS stream
    let segmentDelimiter : Uint8Array | null = null;


    if (remuxTo === 'fmp4') {
      const { writable, readable } = this.services.tsToFragmentedMp4TransmuxStreamFactory();

      // Since the TS-to-FragmentedMp4 transform operates on a continuous TS
      // stream we need to embed the Mimir Segment Delimiter packets in the
      // TS stream so that the transmuxer knows when to flush out a MP4
      // fragment.
      segmentDelimiter = createMimirSegmentDelimiterPacket();

      flushToDiskPromise = readable
        .pipeTo(writableFileStream(logger, vfs, outputFilename, false));

      target = await createStreamTarget(logger, writable, clock);
    } else {
      target = await createFileTarget(vfs, outputFilename);
    }

    const chunkInfos =
      createSourceChunkInfoStreamForSegmentedMedia(
        mediaSegmentsUrlProvider,
        itemId,
        streamRoleId,
        null,
        segmentRange.startIndexInclusive,
        segmentRange.endIndexExclusive,
        logger,
        segmentDelimiter,
      );


    const transferPipeline = this.createTransferPipeline(abortSignal);

    logger.log({ level: 'info', message: 'Starting TS segment download', itemId, outputFilename, streamRoleId, segmentRange, remuxTo });

    await transferPipeline.transfer({ chunkInfos, target, logger, progressId });

    if (flushToDiskPromise !== null) {
      await flushToDiskPromise;
    }

    logger.log({ level: 'info', message: 'Completed TS segment download', itemId, outputFilename, streamRoleId, segmentRange, remuxTo });
  }


  async downloadFragmentedMp4StreamToSingleFile(
    parameters: {
      itemId: string,
      segmentRange: MediaSegmentRange,
      outputFilename: string,
      abortSignal?: AbortSignal,
      progressHandler?: DownloadProgressHandler,
    },
  ): Promise<void> {
    const { mediaSegmentsUrlProvider, vfs } = this.services;
    const { itemId, segmentRange, outputFilename, abortSignal, progressHandler } = parameters;

    const progressId = outputFilename;
    const logger = this.getProgressLogger(progressId, progressHandler);
    const transferPipeline = this.createTransferPipeline(abortSignal);

    const streamRoleId = StreamRoleId.EditProxyFull;

    // Segment 0 of fragmented MP4 is an initialization segment which does not
    // provide any media.
    const initSegmentCount = 1;

    const chunkInfos =
      createSourceChunkInfoStreamForSegmentedMedia(
        mediaSegmentsUrlProvider,
        itemId,
        StreamRoleId.EditProxyFull,
        0,
        segmentRange.startIndexInclusive + initSegmentCount,
        segmentRange.endIndexExclusive + initSegmentCount,
        logger,
        null,
      );

    const target = await createFileTarget(vfs, outputFilename);

    logger.log({ level: 'info', message: 'Starting Fragment mp4 download', itemId, outputFilename, streamRoleId, segmentRange });

    await transferPipeline.transfer({ chunkInfos, target, logger, progressId });

    logger.log({ level: 'info', message: 'Completed Fragment mp4 download', itemId, outputFilename, streamRoleId, segmentRange });
  }

  async downloadMxfSubclipBasedOnProxySegmentRange(
    parameters: {
      itemId: string,
      proxySegmentRange: MediaSegmentRange,
      outputFilename: string,
      abortSignal?: AbortSignal,
      progressHandler?: DownloadProgressHandler,
    },
  ): Promise<void> {
    const { mxfRewrapItemProvider, resourceAccessor, vfs } = this.services;
    const { itemId, proxySegmentRange, outputFilename, abortSignal, progressHandler } = parameters;

    const transferPipeline = this.createTransferPipeline(abortSignal);

    const progressId = outputFilename;
    const logger = this.getProgressLogger(progressId, progressHandler);

    const rewrapServices = {
      logger,
      mxfRewrapItemProvider,
      resourceAccessor,
      vfs,
    };

    logger.log({ level: 'info', message: 'Starting MXF subclip download', itemId, outputFilename, proxySegmentRange });

    await tsSegmentRangeMxfRewrapTransfer(
      rewrapServices,
      transferPipeline,
      itemId,
      proxySegmentRange.startIndexInclusive,
      proxySegmentRange.endIndexExclusive,
      outputFilename
    );

    logger.log({ level: 'info', message: 'Completed MXF subclip download', itemId, outputFilename, proxySegmentRange });
  }

  private getProgressLogger(
    progressId: string,
    progressHandler: DownloadProgressHandler | undefined
  ): StructuredLogger {
    if (!progressHandler) {
      return this.services.logger;
    }
    const progressLogger = new ProgressPropagatingLogger(this.services.logger);
    progressLogger.addHandler(progressId, (progressEntry: ProgressEntry) => {
      const numberOfBytesDownloaded =
        (progressEntry.position && isFinite(progressEntry.position)) ?
          progressEntry.position :
          0;

      const totalNumberOfBytes =
        (progressEntry.length && isFinite(progressEntry.length)) ?
          progressEntry.length :
          'undetermined';

      progressHandler({ numberOfBytesDownloaded, totalNumberOfBytes });
    });
    return progressLogger;
  }

  private createTransferPipeline(abortSignal: AbortSignal | undefined): TransferPipeline {
    const { resourceAccessor, vfs } = this.services;
    return new TransferPipeline(abortSignal ?? this.dummyAbortSignal, resourceAccessor, vfs);
  }
}
